import { gaCampaign, sendFunnelEvent } from './campaignAttribution';
/**
 * Google Analytics 4 and Google Tag Manager Utility Functions
 * 
 * This module provides functions to initialize and track events with GA4 and GTM. Every trackEvent also goes to the
 * privacy-friendly analytics (utils/observability.js, REACT_APP_ANALYTICS) when that is on.
 */
import { stripQuery, trackAnalyticsEvent } from './observability';
import { analyticsAllowed, startAnalytics, analyticsId } from './analyticsConsent';
import { trackRedditEvent, trackRedditPageView } from './redditAds';

// Get the GA4 Measurement ID from environment variables
export const GA4_MEASUREMENT_ID = analyticsId();
export const GTM_ID = process.env.REACT_APP_GTM_ID;

/**
 * Check if GA4 is enabled (measurement ID is configured)
 */
export const isGA4Enabled = () => {
  return !!GA4_MEASUREMENT_ID && analyticsAllowed();
};

/**
 * Initialize Google Analytics 4
 * This should be called once when the app loads
 */
export const initGA4 = () => startAnalytics();

/**
 * Track page views
 * Call this on route changes
 */
export const trackPageView = (path, title) => {
  trackRedditPageView();
  if (!analyticsAllowed()) return;
  sendFunnelEvent('page_view', { page: stripQuery(path) });
  if (!isGA4Enabled() || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: stripQuery(path),
    page_title: title || document.title,
    ...gaCampaign(),
    page_location: stripQuery(window.location.href),
  });

};

/**
 * Track custom events
 * 
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!analyticsAllowed()) return;
  // Output and preset ids go only to our own collector (they link an event to a reviewed demo output), never to GA.
  const ids = Object.fromEntries(['preset_sha256', 'output_id'].filter((k) => typeof eventParams[k] === 'string'
    && (k === 'preset_sha256' ? /^[0-9a-f]{64}$/ : /^[0-9a-f-]{36}$/i).test(eventParams[k])).map((k) => [k, eventParams[k]]));
  const allowed = ['method', 'synth', 'plan', 'currency', 'value', 'form_type', 'file_type', 'audio_type', 'success', 'surface', 'action', 'sound_slot', 'duration_ms', 'error_code', 'sound_count'];
  eventParams = Object.fromEntries(Object.entries(eventParams).filter(([key, value]) => allowed.includes(key) && ['string', 'number', 'boolean'].includes(typeof value)));
  sendFunnelEvent(eventName, { ...eventParams, ...ids });
  trackAnalyticsEvent(eventName, eventParams); // Plausible (REACT_APP_ANALYTICS), non-personal props only
  if (!isGA4Enabled() || !window.gtag) return;

  window.gtag('event', eventName, { ...gaCampaign(), ...eventParams });

};

/**
 * Track user sign up
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', { method });
  trackRedditEvent('SignUp');
};

/**
 * Track user login
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', { method });
};

/**
 * Track purchase
 */
export const trackPurchase = (transactionId, value, currency = 'USD', items = []) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items,
  });
};

/**
 * Track when user begins checkout
 */
export const trackBeginCheckout = (value, currency = 'USD', items = []) => {
  trackEvent('begin_checkout', {
    value: value,
    currency: currency,
    items: items,
  });
};

/**
 * Track product views
 */
export const trackViewItem = (itemId, itemName, value, currency = 'USD') => {
  trackEvent('view_item', {
    items: [{
      item_id: itemId,
      item_name: itemName,
      price: value,
      currency: currency,
    }],
  });
};

/**
 * Track add to cart
 */
export const trackAddToCart = (itemId, itemName, value, currency = 'USD') => {
  trackEvent('add_to_cart', {
    value: value,
    currency: currency,
    items: [{
      item_id: itemId,
      item_name: itemName,
      price: value,
    }],
  });
};

/**
 * Track contact form submissions
 */
export const trackContactForm = (formType) => {
  trackEvent('contact_form_submit', {
    form_type: formType,
  });
};

/**
 * Track audio playback
 */
export const trackAudioPlay = (audioName, audioType) => {
  trackEvent('audio_play', {
    audio_name: audioName,
    audio_type: audioType,
  });
};

/**
 * Track download events
 */
export const trackDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
  });
};

/**
 * Track newsletter subscription
 */
export const trackNewsletterSignup = () => {
  trackEvent('newsletter_signup');
};

/**
 * Track job application
 */
export const trackJobApplication = (jobTitle) => {
  trackEvent('job_application', {
    job_title: jobTitle,
  });
};

/**
 * Set user ID for tracking authenticated users
 */
export const setUserId = () => {}; // Do not send account identifiers to analytics.

/**
 * Set user properties
 */
export const setUserProperties = () => {}; // Do not send account identifiers to analytics.

/**
 * Clear user ID (on logout)
 */
export const clearUserId = () => {}; // Do not send account identifiers to analytics.

// ============================================================================
// Google Tag Manager Functions
// ============================================================================

/**
 * Check if GTM is enabled (container ID is configured)
 * Note: We only check if dataLayer exists, not if GTM_ID is set,
 * because GTM might be loaded via index.html even if env var isn't available at JS build time
 */
export const isGTMEnabled = () => false; // Advertising tags are not enabled.

/**
 * Push event to Google Tag Manager dataLayer
 * 
 * @param {string} eventName - Name of the event
 * @param {object} eventData - Event data object
 */
export const pushGTMEvent = (eventName, eventData = {}) => {
  if (!isGTMEnabled()) {
    console.warn('GTM not enabled or dataLayer not available');
    return;
  }

  window.dataLayer.push({
    event: eventName,
    ...eventData,
  });

};

/**
 * Track beta tester signup with email address
 * This includes user_data for attribution matching (will be hashed by GTM/Reddit)
 * 
 * @param {string} email - User's email address
 */
export const trackBetaTesterSignup = (email) => {
  pushGTMEvent('beta_tester_signup', {
    user_data: {
      email_address: email,
    },
  });
};

